/**
 * audioBus — Minimal audio surface.
 *
 * Only two cues exist:
 *   • playWheel(cue, opts) — wheel-picker ticks/settle/select.
 *     Plays ONLY on coarse-pointer devices (mobile / tablet).
 *   • playSelect()         — selection confirm cue. Plays on all devices.
 *
 * Everything else (hover/tap/transition/ambient/spatial/scenes/sound-toggle)
 * was intentionally removed.
 */

type WheelCue = "tick" | "select" | "settle" | "start" | "end";
type WheelOpts = { velocity?: number; direction?: "up" | "down" };

const isBrowser = typeof window !== "undefined";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

const WHEEL_THROTTLE: Record<WheelCue, number> = {
  tick: 28, select: 80, settle: 120, start: 0, end: 0,
};
const lastWheelAt: Record<WheelCue, number> = {
  tick: 0, select: 0, settle: 0, start: 0, end: 0,
};
let lastSelectAt = 0;

function isCoarsePointer(): boolean {
  if (!isBrowser) return false;
  try { return window.matchMedia("(pointer: coarse)").matches; } catch { return false; }
}

function reducedMotion(): boolean {
  if (!isBrowser) return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

function ensureCtx(): AudioContext | null {
  if (!isBrowser) return null;
  if (ctx) return ctx;
  const AC = (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!AC) return null;
  try {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.9;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.knee.value = 8;
    limiter.ratio.value = 8;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.18;
    master.connect(limiter).connect(ctx.destination);
  } catch {
    ctx = null;
    master = null;
  }
  return ctx;
}

function resume() {
  const c = ensureCtx();
  if (c && c.state === "suspended") c.resume().catch(() => {});
}

function tickVoice(opts?: WheelOpts) {
  const c = ensureCtx();
  if (!c || !master) return;
  const t = c.currentTime;
  const v = Math.max(0, Math.min(1, opts?.velocity ?? 0.3));
  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.value = 1100 + v * 350;
  const g = c.createGain();
  const peak = 0.012 + v * 0.020;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.045);
  osc.connect(g).connect(master);
  osc.start(t);
  osc.stop(t + 0.06);
}

function settleVoice() {
  const c = ensureCtx();
  if (!c || !master) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(640, t);
  osc.frequency.exponentialRampToValueAtTime(420, t + 0.14);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.045, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
  osc.connect(g).connect(master);
  osc.start(t);
  osc.stop(t + 0.22);
}

function selectVoice() {
  const c = ensureCtx();
  if (!c || !master) return;
  const t = c.currentTime;
  // Two-note soft confirm — short, calm, gallery-tone.
  const tones = [880, 1320];
  tones.forEach((f, i) => {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    const g = c.createGain();
    const start = t + i * 0.06;
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.05, start + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.20);
    osc.connect(g).connect(master!);
    osc.start(start);
    osc.stop(start + 0.24);
  });
}

export const audioBus = {
  /** Wheel-picker cues. Coarse-pointer (mobile / tablet) only. */
  playWheel(cue: WheelCue, opts?: WheelOpts) {
    if (!isCoarsePointer()) return;
    if (reducedMotion()) return;
    if (typeof document !== "undefined" && document.hidden) return;
    const now = performance.now();
    if (now - lastWheelAt[cue] < WHEEL_THROTTLE[cue]) return;
    lastWheelAt[cue] = now;
    resume();
    try {
      if (cue === "tick" || cue === "start" || cue === "end") tickVoice(opts);
      else if (cue === "settle") settleVoice();
      else if (cue === "select") selectVoice();
    } catch { /* noop */ }
  },

  /** Selection confirm cue. Plays on all devices. */
  playSelect() {
    if (reducedMotion()) return;
    if (typeof document !== "undefined" && document.hidden) return;
    const now = performance.now();
    if (now - lastSelectAt < 120) return;
    lastSelectAt = now;
    resume();
    try { selectVoice(); } catch { /* noop */ }
  },
};

/** No-op kept so existing imports compile. The sound/haptic side was removed. */
export function hapticTap(_intensity?: "light" | "medium") { /* removed */ }

// One-shot audio unlock on first user gesture (required by browsers).
if (isBrowser) {
  const unlock = () => {
    resume();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("touchstart", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("touchstart", unlock, { passive: true });
  window.addEventListener("keydown", unlock);
}
